import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Avatar } from '@producer/ui-kit';
import { observer } from 'mobx-react-lite';

import { Plus } from '../../components/icons';
import { Space } from '../entities/space';
import { api } from '../../api';
import { getNameInitials } from '../../lib/utils/get-name-initials';

import styles from './space-selector.module.scss';
type SpaceSelectorProps = {
  /**
   * The current space
   */
  space: Space;
  /**
   * To sync Radix dropdown with original drawer
   */
  isOpen: boolean;
  /**
   * Close the drawer
   */
  onClose: () => void;
};

export const SpaceSelector: React.FC<SpaceSelectorProps> = observer(
  ({ space, isOpen, onClose }) => {
    const navigate = useNavigate();
    const spaces = Space.getAll();

    const onCreateSpace = () => {
      onClose();
      const href = '/onboarding/create-workspace';
      navigate(href, { state: { spaceId: space._id } });
    };

    const onSelect = (id: string) => {
      onClose();
      api.updateSpaceInHeaders(id);
      const href = `/space/${id}`;
      navigate(href);
    };

    const [open, setIsOpen] = useState(false);

    useEffect(() => {
      if (!isOpen) {
        setIsOpen(isOpen);
      }
    }, [isOpen]);

    return (
      <div className={styles['container']}>
        <Menu.Root onOpenChange={(open) => setIsOpen(open)} open={open}>
          <Menu.Trigger className={styles['trigger']} asChild>
            <div className={styles['selected-space-wrapper']}>
              <div className={styles['selected-space']}>
                <Avatar
                  size="medium"
                  src={space.avatar}
                  initials={getNameInitials(space.name)}
                  theme="dark"
                />
                <span className={styles['space-name']}>{space.name}</span>
              </div>

              <ChevronDownIcon color="var(--color-grayscale-shuttle, #626878)" />
            </div>
          </Menu.Trigger>

          <Menu.Content className={styles['menu-dialog']} align={'start'} sideOffset={8}>
            <Menu.Item className={styles['menu-item']} onSelect={onCreateSpace}>
              <div className={styles['icon-container']}>
                <div className={styles['icon']}>
                  <Plus />
                </div>
              </div>

              <div className={styles['name']}>Create new workspace</div>
            </Menu.Item>

            <Menu.Separator className={styles['separator']} />

            {spaces.map((el) => {
              const isSelected = el._id === space._id;

              return (
                <Menu.Item
                  className={styles['menu-item']}
                  key={el._id}
                  data-active={isSelected}
                  onClick={() => onSelect(el._id)}
                >
                  <div className={styles['icon-container']} data-active={isSelected}>
                    <Avatar
                      size="small"
                      src={el.avatar}
                      theme="dark"
                      initials={getNameInitials(el.name)}
                    />
                  </div>

                  <div className={styles['name']} data-active={isSelected}>
                    {el.name}
                  </div>
                </Menu.Item>
              );
            })}
          </Menu.Content>
        </Menu.Root>
      </div>
    );
  },
);
